import React from 'react'
import { capitalize, roundWithLocale } from '@sketch/utils'
import { useHandleCopyValue } from '@sketch/modules-common'
import { Flex, Box, Tooltip, Separator } from '@sketch/components'
import { PopoverHeader } from '../../../../PopoverHeader'
import { AttributeList } from '../../../components/Attribute'
import { FullCopyAttribute } from '../../../components/FullCopyAttribute'
import { BlurStops } from './BlurStops'
import { CopyButton, CopyIcon, FormatDetail } from './Blur.styles'
import { Blur as BlurProps, BlurType } from '../../../../../../../inspector'

interface BlurPopoverProps extends BlurProps {
  copyValue: string
  onClose?: () => void
}

export const BlurPopover = ({
  copyValue,
  onClose,
  type,
  center,
  motionAngle,
  radius,
  saturation,
  gradient,
}: BlurPopoverProps) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  const blurStops =
    gradient?.stops.map(item => ({
      position: item.position,
      radius: item.color.alpha || 0,
    })) ?? []

  return (
    <>
      <PopoverHeader
        onClose={onClose}
        title="Blur"
        extraAction={
          <Tooltip content={copyTooltipText} visible={copyTooltipVisible}>
            <Flex alignItems="center">
              <CopyButton
                onClick={handleCopyValue}
                onMouseEnter={handleEnterCopiableArea}
                onMouseLeave={handleLeaveCopiableArea}
              >
                <CopyIcon />
              </CopyButton>
            </Flex>
          </Tooltip>
        }
      />
      {/* @TODO: to be added when we have a way to preview blurs, SWEB-1777 */}
      {/* <SwatchContainer /> */}
      <Box pl={5} width="300px" mt={3}>
        <AttributeList>
          <FullCopyAttribute label="Type" value={capitalize(type)} />
          <FullCopyAttribute
            label="Radius"
            value={
              <>
                {roundWithLocale(radius ?? 0, 0)}
                <FormatDetail>px</FormatDetail>
              </>
            }
          />
          <FullCopyAttribute
            label="Motion Angle"
            value={
              <>
                {roundWithLocale(motionAngle ?? 0, 0)}
                <FormatDetail>º</FormatDetail>
              </>
            }
          />
          <FullCopyAttribute
            label="Saturation"
            value={
              <>
                {roundWithLocale(saturation ?? 0, 0)}
                <FormatDetail>%</FormatDetail>
              </>
            }
          />

          {type === BlurType.Zoom &&
          center &&
          ((center.x && center.y) || center.x === 0 || center.y === 0) ? (
            <FullCopyAttribute
              label="Angle"
              copyValue={`(${center.x}, ${center.y})`}
              value={
                <>
                  {roundWithLocale(center.x, 0)}
                  <FormatDetail>º</FormatDetail>
                  <FormatDetail>, </FormatDetail> {roundWithLocale(center.y, 0)}
                  <FormatDetail>º</FormatDetail>
                </>
              }
            />
          ) : null}
        </AttributeList>
      </Box>
      <Separator mt={2} />
      {blurStops.length > 0 && (
        <Box p={5}>
          <BlurStops stops={blurStops} />
        </Box>
      )}
    </>
  )
}
